import axios from 'axios';
import { useQuery, useMutation, MutationOptions } from 'react-query';

import { createRestApi } from '~/App/Api/rest';
import { UserWithSignatures as User, Signature } from '~/types/models';

export interface SignaturesListQueryProps {
  page: number;
  exclude_default: number;
  includes?: string[];
}

export interface UseUpdateUserSignatureOptions {
  userId: string;
  attributes: {
    signature_id: string;
    type: string;
  };
}

export function useSignaturesList({
  params,
  queryName = 'signatures',
}: {
  params: SignaturesListQueryProps;
  queryName?: string;
}) {
  async function useSignaturesListQuery(queryParams: SignaturesListQueryProps) {
    const api = createRestApi<Signature>('/api/v2/signatures');
    return await api.getAll(queryParams);
  }

  return useQuery([queryName, params], () => useSignaturesListQuery(params));
}

export function useUsersSignatures({
  page,
  queryName = 'user-signatures',
}: {
  page: number;
  queryName?: string;
}) {
  async function useUsersSignaturesQuery({ page }: { page: number }) {
    const api = createRestApi<User>('/api/v2/users');
    return await api.getAll({
      page,
      includes: 'signatures',
    });
  }

  return useQuery(
    [queryName, { page }],
    () => useUsersSignaturesQuery({ page }),
    { keepPreviousData: true }
  );
}

export function useSignatureByUserId({
  userId,
  queryName = 'user-signature',
}: {
  userId: string;
  queryName?: string;
}) {
  async function useSignatureByUserIdQuery({ userId }: { userId: string }) {
    const api = await axios.get<Signature>(`/api/v2/users/${userId}/signature`);
    return api.data;
  }

  return useQuery([queryName, { userId }], () =>
    useSignatureByUserIdQuery({ userId })
  );
}

export function useUpdateUserSignature({
  options,
}: {
  options: MutationOptions<User, UseUpdateUserSignatureOptions, Error, unknown>;
}) {
  async function useUpdateUserSignatureQuery({
    userId,
    attributes,
  }: UseUpdateUserSignatureOptions) {
    const api = createRestApi<User>(`/api/v2/users/${userId}`);

    const { data } = await api.post(attributes);

    return data;
  }
  return useMutation(useUpdateUserSignatureQuery, options);
}

export function useTeamSignatures({
  queryName = 'team-signatures',
}: {
  queryName?: string;
}) {
  async function useTeamSignaturesQuery() {
    const api = createRestApi<User>('/api/v2/teams');
    return await api.getAll({
      includes: 'signatures',
    });
  }

  return useQuery(queryName, useTeamSignaturesQuery);
}
